import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Loader2, Upload } from 'lucide-react';
import VideoRecorder, { RecordedVideo } from '../Testimonials/VideoRecorder';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getAssetUrl } from '../../utils/assetUrl';
import { ActionButton, Button } from '../UI';

export interface CommunityVideo {
  url?: string;
  posterUrl?: string;
  durationSec?: number;
  status?: '' | 'processing' | 'ready' | 'failed';
  error?: string;
}

interface Props {
  communityId: string;
  lang: 'pt' | 'en' | 'es';
  langLabel: string;
  video?: CommunityVideo;
  /** Recarrega as comunidades (status do processamento vem do GET /community/admin). */
  onChanged: () => void;
}

// Mesmos limites do backend (routes/community.js: VIDEO_MAX_SECONDS, VIDEO_MAX_BYTES).
const DURATION_OPTIONS = [60, 120, 180];
const MAX_MB = 250;
const POLL_MS = 5000;

const CommunityVideoEditor: React.FC<Props> = ({ communityId, lang, langLabel, video, onChanged }) => {
  const { showSuccess, showError } = useNotifications();
  const confirm = useConfirm();
  const [recorded, setRecorded] = useState<RecordedVideo | null>(null);
  const [progress, setProgress] = useState<number | null>(null);

  const processing = video?.status === 'processing';

  // Troca de idioma/comunidade descarta a gravação que não foi enviada.
  useEffect(() => {
    setRecorded(null);
  }, [communityId, lang]);

  useEffect(() => {
    if (!processing) return undefined;
    const id = window.setInterval(onChanged, POLL_MS);
    return () => window.clearInterval(id);
  }, [processing, onChanged]);

  const upload = async () => {
    if (!recorded) return;
    const ext = recorded.blob.type.includes('mp4') ? 'mp4' : recorded.blob.type.includes('quicktime') ? 'mov' : 'webm';
    const data = new FormData();
    data.append('video', recorded.blob, recorded.fileName || `apresentacao.${ext}`);
    setProgress(0);
    try {
      await axios.post(`/community/admin/${communityId}/video/${lang}`, data, {
        timeout: 0,
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setRecorded(null);
      showSuccess('Vídeo enviado', 'Ele fica disponível assim que o processamento terminar.');
      onChanged();
    } catch (error: any) {
      const status = error.response?.status;
      showError('Erro ao enviar vídeo', status === 413
        ? 'O servidor recusou o arquivo por tamanho. Tente um vídeo menor.'
        : error.response?.data?.message || 'Não foi possível enviar o vídeo');
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Remover vídeo',
      message: `Remover o vídeo de apresentação em ${langLabel}?`,
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`/community/admin/${communityId}/video/${lang}`);
      showSuccess('Vídeo removido', 'O vídeo de apresentação foi removido.');
      onChanged();
    } catch (error: any) {
      showError('Erro ao remover vídeo', error.response?.data?.message || 'Não foi possível remover o vídeo');
    }
  };

  return (
    <div className="border-t pt-4">
      <p className="text-sm font-medium text-gray-700">Vídeo de apresentação ({langLabel})</p>
      <p className="text-xs text-gray-500 mb-3">
        Aparece na página da oferta entre a descrição e o preço.
        {lang !== 'pt' && ' Sem vídeo neste idioma, a página mostra o vídeo em português.'}
      </p>

      {video?.url && (
        <div className="mb-3">
          <video
            key={video.url}
            src={getAssetUrl(video.url)}
            poster={video.posterUrl ? getAssetUrl(video.posterUrl) : undefined}
            controls
            playsInline
            preload="metadata"
            className="w-full max-w-md rounded-lg bg-black aspect-video"
          />
          <div className="flex items-center gap-3 mt-2">
            {!!video.durationSec && <span className="text-xs text-gray-500 tabular-nums">{Math.round(video.durationSec)}s</span>}
            <ActionButton action="delete" label="Remover vídeo" labelVisibility="always" onClick={remove} />
          </div>
        </div>
      )}

      {processing && (
        <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 mb-3" role="status">
          <Loader2 className="w-4 h-4 animate-spin" />
          Processando o vídeo novo… pode levar alguns minutos.{video?.url && ' O vídeo atual continua no ar até lá.'}
        </div>
      )}

      {video?.status === 'failed' && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2 mb-3" role="alert">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{video.error || 'Não foi possível processar o vídeo.'} Grave ou envie de novo abaixo.</span>
        </div>
      )}

      {!processing && (
        <>
          <VideoRecorder
            value={recorded}
            onChange={setRecorded}
            disabled={progress !== null}
            durationOptions={DURATION_OPTIONS}
            defaultSeconds={180}
            maxMb={MAX_MB}
            title={video?.url ? 'Substituir vídeo' : 'Gravar ou enviar vídeo'}
            hint="Até 3 minutos. Grave com a câmera ou envie um arquivo; vídeos mais longos são cortados na duração escolhida."
          />
          {recorded && (
            <div className="mt-3 flex items-center gap-3">
              <Button type="button" onClick={upload} disabled={progress !== null}>
                {progress !== null ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {progress !== null ? `Enviando… ${progress}%` : 'Enviar vídeo'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommunityVideoEditor;
